import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Crown, MapPin, CalendarDays, Sparkles, Shirt, FileText, Wallet, Phone, Globe, Gem,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const SCHEDULE = [
  { date: "6 August 2026", title: "Arrival & Grooming Begins", venue: "SAJ Earth Resort, Nedumbassery, Kochi",
    points: ["Reporting at 09:00 AM & check-in", "Personality development & catwalk training", "Beauty tips, photo shoots & interactive sessions"] },
  { date: "6 – 10 August 2026", title: "The Royal Residence", venue: "SAJ Earth Resort, Kochi",
    points: ["Accommodation, breakfast, lunch & dinner provided", "Transport arranged from SAJ Resort to the finale venue", "Official grooming continues through the week"] },
  { date: "9 August 2026", title: "The Grand Finale", venue: "Le Méridien, Kochi",
    points: ["The crowning evening of Mrs South India 2026", "Professional makeup & hairstyling by the organizers", "Family & friends welcome in the audience"] },
  { date: "10 August 2026", title: "Farewell & Departure", venue: "Check-out by 12:00 PM",
    points: ["Return tickets advised for 10 August", "A week that changes how you walk forever"] },
];

const SUBCONTESTS = [
  { title: "Mrs Talent", desc: "Showcase your gift on the grand stage — a soundtrack of at least three and not more than four minutes.", icon: Gem },
  { title: "Mrs Fashionista", desc: "A celebration of style and presence. Official dress code: the Red Gown.", icon: Shirt },
  { title: "Mrs Popularity Queen", desc: "Crowned to the contestant with the highest number of votes — rally your admirers.", icon: Sparkles },
];

const DRESS_CODES = [
  { round: "Round 1 — Traditional Wear", detail: "Kancheevaram Saree", note: "The timeless drape of the South, worn with pride." },
  { round: "Round 2 — Saree Gown", detail: "Colour Theme: Black", note: "A full-length black saree gown — modern royalty." },
  { round: "Stilettos", detail: "Black & Nude", note: "Minimum heel height of 3 inches for all rounds." },
];

const ESSENTIALS = [
  "Valid ID proof — Aadhaar, Passport, Driving License or Election card (college ID not accepted)",
  "Costumes, accessories and stilettos for all rounds to be carried by the contestant",
  "A prepared 55-second on-stage introduction",
  "Notepad and pen for the grooming sessions",
  "All travel to and from Kochi is arranged by the contestant",
  "Accommodation is arranged for the contestant only — accompanying family at ₹6,000 per person per day",
  "Valuables remain the owner's responsibility",
  "The decision of the judges is final",
];

export default function EventMrsSouthIndia() {
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div data-testid="event-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
          <Link to="/" data-testid="event-back-home" className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-neutral-300 transition-colors hover:text-[#D4AF37]">
            <ArrowLeft className="h-4 w-4" strokeWidth={1} /> Back to the House
          </Link>
          <span className="font-script text-2xl text-[#D4AF37]">D'La Valentina</span>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-[#D4AF37]/25">
        <img src="/brand/mrs-planet-2017.jpg" alt="Mrs pageant winner" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#050505]/70 to-[#050505]" />
        <div className="relative mx-auto max-w-6xl px-6 py-28 text-center lg:py-36">
          <Reveal>
            <Crown className="mx-auto h-10 w-10 text-[#D4AF37]" strokeWidth={1} />
            <span className="font-script mt-6 block text-3xl text-[#D4AF37] md:text-4xl">Brandrootz Presents · A Pegasus Event</span>
            <h1 data-testid="event-title" className="font-display mt-4 text-5xl font-medium md:text-7xl">
              Mrs South India <span className="gold-gradient-text">2026</span>
            </h1>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.3em] text-neutral-300">
              <span data-testid="event-date" className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} /> Grand Finale — 9 August 2026
              </span>
              <span data-testid="event-venue" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} /> Le Méridien, Kochi
              </span>
            </div>
            <a href="/register" data-testid="event-register-button" className="mt-12 inline-flex items-center gap-3 bg-[#D4AF37] px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#F9F1CC]">
              <Crown className="h-4 w-4" strokeWidth={1.5} /> Register Now
            </a>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <Reveal className="mb-16">
          <span className="font-script text-3xl text-[#D4AF37]">The Royal Itinerary</span>
          <h2 className="font-display mt-3 text-4xl font-medium md:text-5xl">Five Days in Kochi</h2>
        </Reveal>
        <div className="space-y-6">
          {SCHEDULE.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`schedule-item-${i}`} className="grid gap-6 border border-white/10 bg-[#0C0C0C] p-8 transition-colors duration-500 hover:border-[#D4AF37]/50 md:grid-cols-4">
                <div>
                  <span className="font-display gold-gradient-text text-2xl">{s.date}</span>
                  <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    <MapPin className="h-3 w-3" strokeWidth={1.5} /> {s.venue}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <h3 className="font-display text-2xl text-white">{s.title}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {s.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm font-light text-neutral-400">
                        <span className="mt-1.5 h-1 w-1 shrink-0 bg-[#D4AF37]" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#0A0A0A]">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal className="mb-16 text-center">
            <span className="font-script text-3xl text-[#D4AF37]">Beyond the Crown</span>
            <h2 className="font-display mt-3 text-4xl font-medium md:text-5xl">The Sub-Contests</h2>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {SUBCONTESTS.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`subcontest-${i}`} className="h-full border border-[#D4AF37]/20 bg-[#0C0C0C] p-8 text-center transition-all duration-500 hover:-translate-y-2 hover:border-[#D4AF37]/60">
                  <s.icon className="mx-auto h-8 w-8 text-[#D4AF37]" strokeWidth={1} />
                  <h3 className="font-display mt-6 text-2xl text-white">{s.title}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-neutral-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <Reveal className="mb-16">
          <span className="font-script text-3xl text-[#D4AF37]">The Final Eve</span>
          <h2 className="font-display mt-3 text-4xl font-medium md:text-5xl">Official Dress Code</h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {DRESS_CODES.map((d, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`dresscode-${i}`} className="h-full border-l-2 border-[#D4AF37]/50 bg-[#0C0C0C] p-8">
                <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{d.round}</span>
                <div className="font-display mt-3 text-2xl text-[#D4AF37]">{d.detail}</div>
                <p className="mt-3 text-sm font-light text-neutral-400">{d.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#0A0A0A]">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <Reveal className="mb-12">
            <span className="font-script text-3xl text-[#D4AF37]">Before You Arrive</span>
            <h2 className="font-display mt-3 text-4xl font-medium md:text-5xl">Essentials & Notes</h2>
          </Reveal>
          <div className="grid gap-x-12 gap-y-4 md:grid-cols-2">
            {ESSENTIALS.map((e, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-4 border-b border-white/5 py-4">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="text-sm font-light leading-relaxed text-neutral-300">{e}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div data-testid="event-fee-card" className="h-full border border-[#D4AF37]/30 bg-[#0C0C0C] p-10">
              <Wallet className="h-7 w-7 text-[#D4AF37]" strokeWidth={1} />
              <h3 className="font-display mt-5 text-3xl text-white">Registration Fee</h3>
              <div className="font-display gold-gradient-text mt-4 text-5xl">₹25,000 <span className="text-2xl">+ 18% GST</span></div>
              <p className="mt-4 text-sm font-light leading-relaxed text-neutral-400">
                Includes hotel accommodation, all meals and transportation to and from the finale venue.
                Receipt issued on receipt of payment.
              </p>
              <div className="mt-8 space-y-2 border-t border-white/10 pt-6 text-sm font-light text-neutral-300">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Account Details</div>
                <div>Brandrootz Communications Pvt Ltd</div>
                <div>Indian Overseas Bank · A/C 130302000001025</div>
                <div>IFSC: IOBA0001496</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="event-contact-card" className="flex h-full flex-col justify-between border border-white/10 bg-[#0C0C0C] p-10">
              <div>
                <Phone className="h-7 w-7 text-[#D4AF37]" strokeWidth={1} />
                <h3 className="font-display mt-5 text-3xl text-white">Event Helpline</h3>
                <div className="mt-6 space-y-4 text-sm font-light text-neutral-300">
                  <a href="tel:+919288800999" data-testid="event-helpline" className="flex items-center gap-3 transition-colors hover:text-[#D4AF37]">
                    <Phone className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} /> +91 92888 00999 — Jebitha Ajit
                  </a>
                  <a href="https://www.mrssouthindia.com" target="_blank" rel="noopener noreferrer" data-testid="event-website" className="flex items-center gap-3 transition-colors hover:text-[#D4AF37]">
                    <Globe className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.5} /> www.mrssouthindia.com
                  </a>
                </div>
              </div>
              <a href="/register" data-testid="event-register-button-2" className="mt-10 flex items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#F9F1CC]">
                <Crown className="h-4 w-4" strokeWidth={1.5} /> Claim Your Place in Kochi
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
